import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Package,
  FileText,
  ArrowDownRight,
  ArrowUpRight,
  Wallet,
  FileBarChart,
  Menu,
  X,
  BookOpenText,
  Settings as SettingsIcon,
  Download,
  Users,
  UserCog,
  LogOut,
  BadgeDollarSign,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", roles: ["owner", "admin", "kasir"], menuKey: "dashboard" },
  { to: "/barang", label: "Barang", icon: Package, testId: "nav-products", roles: ["owner", "admin", "kasir"], menuKey: "products" },
  { to: "/faktur", label: "Faktur Penjualan", icon: FileText, testId: "nav-invoices", roles: ["owner", "admin", "kasir"], menuKey: "invoices" },
  { to: "/pelanggan", label: "Data Pelanggan", icon: Users, testId: "nav-customers", roles: ["owner", "admin", "kasir"], menuKey: "customers" },
  { to: "/sales", label: "Data Sales", icon: BadgeDollarSign, testId: "nav-sales", roles: ["owner", "admin", "kasir"], menuKey: "sales_data" },
  { to: "/masuk", label: "Stok Masuk", icon: ArrowDownRight, testId: "nav-in", roles: ["owner", "admin", "kasir"], menuKey: "in" },
  { to: "/keluar", label: "Stok Keluar", icon: ArrowUpRight, testId: "nav-out", roles: ["owner", "admin", "kasir"], menuKey: "out" },
  { to: "/modal", label: "Modal", icon: Wallet, testId: "nav-capital", roles: ["owner", "admin", "kasir"], menuKey: "capital" },
  { to: "/laporan", label: "Laporan", icon: FileBarChart, testId: "nav-reports", roles: ["owner", "admin", "kasir"], menuKey: "reports" },
  { to: "/pengguna", label: "Kelola Pengguna", icon: UserCog, testId: "nav-users", roles: ["owner"] },
  { to: "/pengaturan", label: "Pengaturan", icon: SettingsIcon, testId: "nav-settings", roles: ["owner", "admin", "kasir"], menuKey: "settings" },
];

const NavItem = ({ item, onClick }) => (
  <NavLink
    to={item.to}
    onClick={onClick}
    data-testid={item.testId}
    className={({ isActive }) =>
      cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150",
        isActive
          ? "bg-zinc-900 text-white"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      )
    }
  >
    <item.icon className="h-4 w-4 shrink-0" />
    <span>{item.label}</span>
  </NavLink>
);

const PageTitle = ({ items }) => {
  const { pathname } = useLocation();
  const current = items.find((n) => pathname.startsWith(n.to));
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-zinc-500">Pembukuan</span>
      <span className="text-zinc-300">/</span>
      <span className="text-sm font-semibold text-zinc-900">{current?.label || "Home"}</span>
    </div>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { user, logout, hasMenuAccess } = useAuth();
  const role = user?.role || "kasir";
  const nav = NAV_ITEMS.filter((item) => {
    if (!item.roles.includes(role)) return false;
    if (item.menuKey && !hasMenuAccess(item.menuKey)) return false;
    return true;
  });

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get("/export/xlsx?report=all", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `buku-stok-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch (err) {
      toast.error("Gagal export Excel");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-zinc-200 bg-white">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-zinc-200">
          <div className="h-8 w-8 rounded-md bg-zinc-900 flex items-center justify-center">
            <BookOpenText className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-display font-bold text-zinc-900 leading-tight">Buku Stok</div>
            <div className="text-[11px] text-zinc-500 leading-tight">Modal & Persediaan</div>
          </div>
        </div>
        <nav className="p-3 flex-1 space-y-1">
          {nav.map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        </nav>
        <div className="p-3 text-[11px] text-zinc-400 border-t border-zinc-100">
          Single-user demo · IDR
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-backdrop"
          />
          <div className="absolute left-0 top-0 h-full w-64 bg-white border-r border-zinc-200 flex flex-col">
            <div className="h-16 flex items-center justify-between px-5 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-zinc-900 flex items-center justify-center">
                  <BookOpenText className="h-4 w-4 text-white" />
                </div>
                <div className="text-sm font-display font-bold">Buku Stok</div>
              </div>
              <button
                data-testid="mobile-nav-close"
                onClick={() => setOpen(false)}
                className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-zinc-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="p-3 flex-1 space-y-1">
              {nav.map((item) => (
                <NavItem key={item.to} item={item} onClick={() => setOpen(false)} />
              ))}
            </nav>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-zinc-200 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden h-9 w-9 flex items-center justify-center rounded-md hover:bg-zinc-100"
              onClick={() => setOpen(true)}
              data-testid="mobile-nav-open"
            >
              <Menu className="h-5 w-5" />
            </button>
            <PageTitle items={nav} />
          </div>
          <div className="flex items-center gap-3">
            {role === "owner" && (
              <Button
                data-testid="btn-global-export-xlsx"
                size="sm"
                variant="outline"
                onClick={exportExcel}
                disabled={exporting}
                className="h-8 rounded-md"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {exporting ? "Menyiapkan…" : "Export Excel"}
              </Button>
            )}
            <div className="text-xs text-zinc-500 hidden sm:block">
              {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            {user && (
              <div
                className="hidden sm:flex items-center gap-2 pl-3 border-l border-zinc-200"
                data-testid="user-menu"
              >
                <div className="text-right leading-tight">
                  <div className="text-xs font-medium text-zinc-800 truncate max-w-[160px]">
                    {user.email}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">
                    {user.role}
                  </div>
                </div>
                <Button
                  data-testid="btn-logout"
                  size="icon"
                  variant="ghost"
                  onClick={logout}
                  title="Keluar / Logout"
                  className="h-8 w-8 text-zinc-500 hover:text-rose-600 hover:bg-rose-50"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
